import React, { useState } from 'react'

import { RefreshCw } from 'lucide-react'

import { Button } from '@/components/common'
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs'
import { PageContainer } from '@/layouts'

import {
  AlertasInactividad,
  AlertasVencimiento,
  FaseDistribucion,
  KpiGrid,
  PrioritariosTable
} from '../components/Dashboard'
import { useDashboardStats } from '../hooks/useDashboardStats'

export const DashboardPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs()
  React.useEffect(() => {
    setBreadcrumbs([{ label: 'Dashboard' }])
  }, [setBreadcrumbs])

  const [umbralInactividad, setUmbralInactividad] = useState(7)

  const { stats, isLoading, isError, error, refetch, isFetching } = useDashboardStats({
    inactividad_dias: umbralInactividad,
    vencimiento_dias: 30
  })

  if (isLoading) {
    return (
      <PageContainer size="xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-gray-500">Cargando estadísticas del dashboard...</p>
        </div>
      </PageContainer>
    )
  }

  if (isError || !stats) {
    return (
      <PageContainer size="xl">
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
          <p className="text-gray-600">
            {error ?? 'No se pudieron cargar las estadísticas del dashboard.'}
          </p>
          <Button onClick={() => refetch()} variant="ghost" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            Reintentar
          </Button>
        </div>
      </PageContainer>
    )
  }

  const totalOngoing = stats.resumen_estados['ongoing'] ?? 0

  return (
    <PageContainer
      size="xl"
      title="Dashboard operacional"
      description="Estado actual del proceso de assessments"
      actions={
        <Button
          onClick={() => refetch()}
          variant="ghost"
          size="sm"
          disabled={isFetching}
          className="flex items-center gap-1.5"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      }
    >
      <div className="space-y-8">
        {/* KPIs de estado */}
        <KpiGrid
          resumenEstados={stats.resumen_estados}
          produccionReciente={stats.produccion_reciente}
        />

        {/* Desglose por fase + producción reciente */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            {totalOngoing > 0 ? (
              <FaseDistribucion
                desgloseFases={stats.desglose_fases_ongoing}
                totalOngoing={totalOngoing}
              />
            ) : (
              <p className="text-sm text-gray-400 italic">
                No hay assessments en curso actualmente.
              </p>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Producción últimos {stats.produccion_reciente.periodo_dias} días
            </h3>
            <div className="flex gap-6">
              <div>
                <p className="text-2xl font-bold text-emerald-700">
                  {stats.produccion_reciente.completed}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Completados</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-700">
                  {stats.produccion_reciente.finished}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Finalizados</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-primary-700">
                  {stats.produccion_reciente.total}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Alertas de inactividad */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <AlertasInactividad
            alertas={stats.alertas_inactividad}
            umbralDias={umbralInactividad}
            onUmbralChange={setUmbralInactividad}
          />
        </div>

        {/* Alertas de vencimiento (solo si hay alguna) */}
        {(stats.alertas_vencimiento.vencidos.total > 0 ||
          stats.alertas_vencimiento.criticos.total > 0 ||
          stats.alertas_vencimiento.en_riesgo.total > 0) && (
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <AlertasVencimiento alertas={stats.alertas_vencimiento} />
          </div>
        )}

        {/* Listado de assessments prioritarios */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Assessments prioritarios
            <span className="ml-2 text-xs font-normal text-gray-400">
              ordenados por urgencia combinada
            </span>
          </h3>
          <PrioritariosTable items={stats.prioritarios} />
        </div>
      </div>
    </PageContainer>
  )
}
